/**
 * Every write the deal surface performs.
 *
 * Three rules from docs/16 hold this file together:
 *
 *  - **Writes are never retried** (section 8.3). TanStack does not retry
 *    mutations by default; `retry: false` says so out loud rather than relying
 *    on a default that could change.
 *  - **Nothing here predicts the engine** (section 8.5). Each endpoint returns
 *    the recomputed DealCardOut, so the server's verdict is written into the
 *    cache and the board is invalidated. Stage, priority, recommendation and
 *    re-evaluation are read from that response, never anticipated locally.
 *  - **The cache is a cache** (section 7.0). Writing the server's own response
 *    into it is caching; deriving a new business value would not be.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import { apiDelete, apiPatch, apiPost } from '../client'
import { callHistoryKey, dealKey } from './dealDetail'
import { dealsKey } from './deals'
import type { DealCard, DecisionValue, Participant } from '../types'

/**
 * One place where a write's result lands: the deal it changed, and the board,
 * because a decision, an outcome or an archive changes which column a card sits
 * in and what the summary tiles count.
 */
function absorb(client: QueryClient, card: DealCard): void {
  client.setQueryData(dealKey(card.id), card)
  void client.invalidateQueries({ queryKey: dealsKey })
}

export function useRecordDecision(dealId: string) {
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: (decision: DecisionValue) =>
      apiPost<DealCard>(`/deals/${dealId}/decisions`, { decision, reason: null }),
    onSuccess: (card) => {
      absorb(client, card)
      // A decision is recorded against the latest call, so the call history now
      // shows something it did not a moment ago.
      void client.invalidateQueries({ queryKey: callHistoryKey(dealId) })
    },
  })
}

export function useSaveNotes(dealId: string) {
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: (userNotes: string) =>
      apiPatch<DealCard>(`/deals/${dealId}/notes`, { user_notes: userNotes }),
    onSuccess: (card) => absorb(client, card),
  })
}

export interface LeadIdentity {
  company_name: string
  country: string | null
  contact_email: string | null
}

/**
 * Lead details and the people on the calls save together, because to the user
 * that is one edit: two endpoints, one form, one Save.
 *
 * The order matters. Participants go second and their response is the one
 * absorbed: confirming a decision maker re-derives stage, priority and
 * recommendation server-side, so that response is the fresher truth. Nothing is
 * validated here first — an empty company name is answered by the backend's own
 * 422, shown on the field (docs/16 section 9.1), rather than by a second copy of
 * the rule living in the browser.
 */
export function useSaveLeadDetails(dealId: string) {
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: async ({ identity, participants }: { identity: LeadIdentity; participants: Participant[] }) => {
      await apiPatch<DealCard>(`/deals/${dealId}`, identity)
      return apiPatch<DealCard>(`/deals/${dealId}/participants`, { participants })
    },
    onSuccess: (card) => absorb(client, card),
  })
}

export interface OutcomeChange {
  outcome: 'won' | 'lost' | null
  lost_reason: string | null
}

/** Won or lost, or reopened. The backend also archives the deal on an outcome
 *  and un-archives it on reopen; the screen reads that back, it does not mirror
 *  the rule. */
export function useSetOutcome(dealId: string) {
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: (change: OutcomeChange) => apiPost<DealCard>(`/deals/${dealId}/outcome`, change),
    onSuccess: (card) => absorb(client, card),
  })
}

export function useSetArchived(dealId: string) {
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: (archived: boolean) =>
      apiPost<DealCard>(`/deals/${dealId}/${archived ? 'archive' : 'unarchive'}`),
    onSuccess: (card) => absorb(client, card),
  })
}

export function useDeleteDeal(dealId: string) {
  const client = useQueryClient()
  return useMutation({
    retry: false,
    mutationFn: () => apiDelete(`/deals/${dealId}`),
    onSuccess: () => {
      client.removeQueries({ queryKey: dealKey(dealId) })
      client.removeQueries({ queryKey: callHistoryKey(dealId) })
      void client.invalidateQueries({ queryKey: dealsKey })
    },
  })
}

